// Durable token storage for the taskpane (PLAN-agent-v2 §1.3, amended).
// Ported from peerception-excel-add-in-modelling/src/taskpane/services/tokenStore.ts —
// keep the two in sync.
//
// Why this exists: the tokens are NEVER sent as cookies — every request carries
// `Authorization: <type> <token>` (ApiService.authHeader, used by both `request` and the
// SSE stream). `document.cookie` is therefore a *storage* mechanism here, not an auth
// mechanism, and it is not reliable in every Office host:
//
//   - Where Office renders the taskpane in a cross-site iframe (Excel on the web, and
//     Windows hosts depending on the WebView2 shim), the add-in origin is a third-party
//     context. A cookie written without `SameSite=None; Secure` is silently rejected by
//     Chromium, and even with it, Edge/Brave third-party-cookie blocking can drop it.
//   - The write then reads back as `null` microseconds later, `authHeader()` goes out
//     empty, the session probe 401s, refresh finds no refreshToken and the app falls back
//     to the login screen. This exact failure mode broke AppSource certification for the
//     sibling modelling add-in on Edge 152.0.4191.66.
//
// So: cookie first (it is the only store a parent-domain/browser-handoff path could read),
// but verified by read-back, with localStorage and then memory behind it. Reads check all
// three so a session survives whichever store actually accepted the write.
import { getCookie, setCookie, removeCookie } from './Cookies';

/** The only keys this store holds. Kept explicit so `clear()` never has to guess. */
export const TOKEN_KEYS = ['accessToken', 'tokenType', 'refreshToken'] as const;
export type TokenKey = (typeof TOKEN_KEYS)[number];

/** Last-resort store: survives re-renders but not a taskpane reload. Always written. */
const memory = new Map<string, string>();

/**
 * Office requires add-in web storage to be namespaced by `partitionKey`, otherwise two
 * users on the same machine share one localStorage bucket and inherit each other's
 * session. Absent outside Office (dev in a plain browser, jsdom) — fall back to a
 * constant prefix there.
 */
function storageKey(name: string): string {
  let partition = '';
  try {
    partition = (globalThis as { Office?: { context?: { partitionKey?: string } } }).Office?.context?.partitionKey ?? '';
  } catch {
    /* Office not present (dev browser / tests) → unpartitioned prefix */
  }
  return partition ? `pcp:${partition}:${name}` : `pcp:${name}`;
}

/** localStorage throws outright in some embedded/partitioned webviews — never let it escape. */
function lsGet(name: string): string | null {
  try {
    return localStorage.getItem(storageKey(name));
  } catch {
    return null;
  }
}

function lsSet(name: string, value: string): void {
  try {
    localStorage.setItem(storageKey(name), value);
  } catch {
    /* storage blocked → memory is still holding the value */
  }
}

function lsRemove(name: string): void {
  try {
    localStorage.removeItem(storageKey(name));
  } catch {
    /* nothing to do */
  }
}

/**
 * Write a token to every store that will accept it.
 *
 * The cookie is attempted first and *verified by reading it back*: a rejected cookie write
 * is silent, so the read-back is the only way to know. localStorage and memory are written
 * unconditionally, so a later cookie eviction (Edge clearing third-party cookies mid-session)
 * still leaves a usable session behind.
 */
export function writeToken(name: TokenKey, value: string, days: number): void {
  memory.set(name, value);
  setCookie(name, value, days);
  lsSet(name, value);
}

/** Read a token from whichever store kept it: cookie → localStorage → memory. */
export function readToken(name: TokenKey): string | null {
  return getCookie(name) ?? lsGet(name) ?? memory.get(name) ?? null;
}

/** True when the cookie write survived — used by diagnostics, not by the auth path. */
export function cookiesUsable(): boolean {
  const probe = '__pcp_probe';
  setCookie(probe, '1', 1);
  const ok = getCookie(probe) === '1';
  removeCookie(probe);
  return ok;
}

/** Drop the session from all three stores (logout, and refresh-token reuse detection). */
export function clearTokens(): void {
  for (const name of TOKEN_KEYS) {
    memory.delete(name);
    removeCookie(name);
    lsRemove(name);
  }
}
